import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { UsageEventsApi } from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';

// Reusable small util
const parseQuery = (search) => {
  const params = new URLSearchParams(search || '');
  const obj = {};
  for (const [k,v] of params.entries()) obj[k] = v;
  return obj;
};

const defaultRangeDays = 30;

export default function UserUsageEventsDashboard() {
  const { user } = useAuth();
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const query = parseQuery(location.search);

  // Initialize date range (wider by default than generic dashboard)
  const [filters, setFilters] = useState(() => {
    const to = new Date();
    const from = new Date(Date.now() - defaultRangeDays * 86400000);
    return {
      from: query.from || from.toISOString(),
      to: query.to || to.toISOString(),
      eventType: query.eventType || '',
      sessionId: query.sessionId || '',
      correlationId: query.correlationId || '',
      page: Number(query.page || 0),
      size: Number(query.size || 50)
    };
  });
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshIndex, setRefreshIndex] = useState(0);

  const isAdmin = user?.roles?.some(r => r.roleName === 'admin');

  const loadData = useCallback(async () => {
    if (!userId) return; // cannot load without a userId
    setLoading(true); setError(null);
    try {
      const emptyOrNone = (v) => {
        if (v == null) return undefined;
        const trimmed = String(v).trim();
        if (!trimmed || trimmed.toLowerCase() === 'none') return undefined;
        return trimmed;
      };
      const resp = await UsageEventsApi.userSearch({
        userId,
        eventType: emptyOrNone(filters.eventType),
        sessionId: emptyOrNone(filters.sessionId),
        correlationId: emptyOrNone(filters.correlationId),
        from: filters.from,
        to: filters.to,
        page: filters.page,
        size: filters.size
      });
      if (resp?.ok) {
        let json = null; try { json = await resp.json(); } catch { json = null; }
        setPageData(json);
      } else {
        setPageData(null);
        setError(new Error(`Request failed (${resp?.status})`));
      }
    } catch (e) {
      setError(e);
      setPageData(null);
    } finally {
      setLoading(false);
    }
  }, [userId, filters]);

  useEffect(() => { loadData(); }, [loadData, refreshIndex]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters(f => ({ ...f, [name]: value }));
  };

  const handleDateQuick = (days) => {
    const to = new Date();
    const from = new Date(Date.now() - days * 86400000);
    setFilters(f => ({ ...f, from: from.toISOString(), to: to.toISOString() }));
  };

  const applyFilters = (e) => {
    e.preventDefault();
    // Sync query string for deep link
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k,v]) => {
      if (v == null) return;
      const trimmed = String(v).trim();
      if (!trimmed || trimmed.toLowerCase() === 'none') return; // skip empty or 'none'
      params.set(k, trimmed);
    });
    navigate({ pathname: `/settings/usage-events/user/${userId}`, search: `?${params.toString()}` });
    setRefreshIndex(i => i + 1);
  };

  const changePage = (delta) => {
    setFilters(f => ({ ...f, page: Math.max(0, f.page + delta) }));
  };

  const totalPages = pageData?.totalPages || 1;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = pageData?.content || pageData?.items || []; // prefer new 'content' field

  const resetFilters = () => {
    setFilters(f => ({ ...f, eventType:'', sessionId:'', correlationId:'', page:0 }));
    setRefreshIndex(i => i + 1);
  };

  const refresh = () => setRefreshIndex(i => i + 1);

  // Quick summary counts
  const distinctEventTypes = useMemo(() => {
    const set = new Set(items.map(i => i.eventType));
    return set.size;
  }, [items]);

  return (
    <div className="settings-container">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.75rem', flexWrap:'wrap' }}>
        <h2 style={{ margin:0 }}>User Usage Events</h2>
        <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
          <button onClick={refresh} disabled={loading}>{loading ? 'Loading…' : '🔄 Refresh'}</button>
          <button className="secondary" onClick={() => handleDateQuick(1)}>24h</button>
          <button className="secondary" onClick={() => handleDateQuick(7)}>7d</button>
          <button className="secondary" onClick={() => handleDateQuick(30)}>30d</button>
          <button className="secondary" onClick={resetFilters}>Clear Filters</button>
        </div>
      </div>
      <div style={{ marginTop:'0.75rem', fontSize:'.7rem', color:'var(--text-muted)' }}>Viewing events for user <strong>{userId}</strong>{isAdmin ? '' : ' (admin scope required for other users)'}.</div>

      <div style={{ marginTop:'1rem', background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)' }}>
        <form onSubmit={applyFilters} style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:'0.75rem' }}>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>From</label>
            <input name="from" value={filters.from} onChange={handleChange} />
          </div>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>To</label>
            <input name="to" value={filters.to} onChange={handleChange} />
          </div>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>Event Type</label>
            <input name="eventType" value={filters.eventType} onChange={handleChange} placeholder="None" />
          </div>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>Session ID</label>
            <input name="sessionId" value={filters.sessionId} onChange={handleChange} placeholder="None" />
          </div>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>Correlation ID</label>
            <input name="correlationId" value={filters.correlationId} onChange={handleChange} placeholder="None" />
          </div>
          <div>
            <label style={{ fontSize:'.7rem', fontWeight:600 }}>Page Size</label>
            <select name="size" value={filters.size} onChange={handleChange}>
              {[25,50,100,250].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ display:'flex', alignItems:'flex-end' }}>
            <button type="submit" style={{ width:'100%' }}>Apply</button>
          </div>
        </form>
      </div>

      {error && <div style={{ marginTop:'1rem', color:'#c00' }}>{error.message}</div>}
      {loading && <div style={{ marginTop:'1rem' }}>Loading events…</div>}

      {!loading && pageData && (
        <>
          <div style={{ marginTop:'1rem', display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:'0.75rem' }}>
            <MiniStat label="Events Loaded" value={(items.length || 0).toLocaleString()} />
            <MiniStat label="Distinct Types" value={distinctEventTypes.toLocaleString()} />
            {pageData.totalElements != null && <MiniStat label="Total Matching" value={pageData.totalElements.toLocaleString()} />}
          </div>

          <div style={{ marginTop:'1.5rem', overflowX:'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Type</th>
                  <th>Session</th>
                  <th>Correlation</th>
                  <th>Payload</th>
                </tr>
              </thead>
              <tbody>
                {items.map(ev => (
                  <tr key={ev.id}>
                    <td>{new Date(ev.occurredAt || ev.createdAt).toLocaleString()}</td>
                    <td>{ev.eventType}</td>
                    <td title={ev.sessionId}>{(ev.sessionId||'').slice(0,12)}</td>
                    <td title={ev.correlationId}>{(ev.correlationId||'').slice(0,12)}</td>
                    <td style={{ maxWidth:360, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{ev.payloadJson}</td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign:'center', fontSize:'.75rem', padding:'0.75rem' }}>No events for filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop:'0.75rem', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'0.5rem' }}>
            <div style={{ fontSize:'.65rem', color:'#555' }}>Page {(pageData.page ?? filters.page) + 1} of {totalPages} | Total: {pageData.totalElements?.toLocaleString?.()}</div>
            <div style={{ display:'flex', gap:'0.5rem' }}>
              <button className="secondary" disabled={(pageData.page ?? filters.page) === 0} onClick={() => changePage(-1)}>Prev</button>
              <button className="secondary" disabled={pageData.last} onClick={() => changePage(1)}>Next</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div style={{ background:'var(--card-bg)', border:'1px solid var(--border-light)', borderRadius:'12px', padding:'0.6rem 0.9rem', display:'flex', flexDirection:'column', gap:'0.25rem', boxShadow:'var(--shadow-sm)' }}>
      <div style={{ fontSize:'.55rem', fontWeight:600, letterSpacing:'0.05em', textTransform:'uppercase', color:'var(--text-muted)' }}>{label}</div>
      <div style={{ fontSize:'1rem', fontWeight:600 }}>{value}</div>
    </div>
  );
}
